import {
  FormsApiConfigurationError,
  getFormsApiConfig,
} from '../config/api'

const SITE =
  'diamond-suites-ocala' as const

const WEBSITE_NAME =
  'Diamond Suites Ocala' as const

type SecurityFields = {
  website: string
  consent: boolean
  recaptchaToken: string
}

export type ContactFormInput =
  SecurityFields & {
    name: string
    email: string
    phone: string
    subject: string
    message: string
  }

export type InquiryFormInput =
  SecurityFields & {
    name: string
    email: string
    phone: string
    serviceType: string
    otherServiceDescription: string
    message: string
  }

type ServerSecurityFields = {
  website: string
  recaptchaToken: string
}

export type ContactFormPayload =
  ServerSecurityFields & {
    formType: 'contact'
    site: typeof SITE
    websiteName: typeof WEBSITE_NAME
    pageUrl: string
    submittedAt: string
    name: string
    email: string
    phone: string
    subject: string
    message: string
  }

export type InquiryFormPayload =
  ServerSecurityFields & {
    formType: 'inquiry'
    site: typeof SITE
    websiteName: typeof WEBSITE_NAME
    pageUrl: string
    submittedAt: string
    name: string
    email: string
    phone: string
    serviceType: string
    otherServiceDescription: string
    message: string
  }

type FormsApiResponse = {
  ok?: boolean
  message?: string
  requestId?: string

  error?: {
    message?: string
  }
}

export class FormsApiError extends Error {
  constructor(
    message: string,

    readonly category:
      | 'configuration'
      | 'validation'
      | 'rate-limit'
      | 'server'
      | 'network',
  ) {
    super(message)

    this.name =
      'FormsApiError'
  }
}

function submissionMetadata() {
  return {
    site:
      SITE,

    websiteName:
      WEBSITE_NAME,

    pageUrl:
      window.location.href,

    submittedAt:
      new Date()
        .toISOString(),
  }
}

function requireConsent(
  consent: boolean,
) {
  if (!consent) {
    throw new FormsApiError(
      'Please agree to the Privacy Policy before submitting.',
      'validation',
    )
  }
}

async function postForm(
  url: string,

  payload:
    | ContactFormPayload
    | InquiryFormPayload,
) {
  let response:
    Response

  try {
    response =
      await fetch(
        url,
        {
          method:
            'POST',

          headers: {
            'Content-Type':
              'application/json',
          },

          body:
            JSON.stringify(
              payload,
            ),

          signal:
            AbortSignal.timeout(
              20_000,
            ),
        },
      )
  } catch {
    throw new FormsApiError(
      'We could not connect to the secure form service. Please check your connection and try again.',
      'network',
    )
  }

  const data =
    (await response
      .json()
      .catch(
        () =>
          null,
      )) as
      | FormsApiResponse
      | null

  if (
    response.ok &&
    data?.ok !==
      false
  ) {
    return data
  }

  if (
    [
      400,
      409,
      413,
      415,
      422,
    ].includes(
      response.status,
    )
  ) {
    throw new FormsApiError(
      data?.error
        ?.message ||
        data?.message ||
        'Please review the form fields and try again.',

      'validation',
    )
  }

  if (
    response.status ===
    429
  ) {
    throw new FormsApiError(
      'Too many messages were submitted. Please wait a few minutes before trying again.',
      'rate-limit',
    )
  }

  throw new FormsApiError(
    'We could not send your message right now. Please try again later.',
    'server',
  )
}

function configurationError(
  error: unknown,
): never {
  if (
    error instanceof
    FormsApiConfigurationError
  ) {
    const message =
      import.meta.env.DEV
        ? error.message
        : 'Online form delivery is temporarily unavailable. Please contact us by phone or email.'

    throw new FormsApiError(
      message,
      'configuration',
    )
  }

  throw error
}

export async function submitContactForm(
  input:
    ContactFormInput,
) {
  try {
    requireConsent(
      input.consent,
    )

    const payload:
      ContactFormPayload =
      {
        formType:
          'contact',

        ...submissionMetadata(),

        name:
          input.name,

        email:
          input.email,

        phone:
          input.phone,

        subject:
          input.subject,

        message:
          input.message,

        website:
          input.website,

        recaptchaToken:
          input.recaptchaToken,
      }

    return await postForm(
      getFormsApiConfig()
        .inquiryUrl,

      payload,
    )
  } catch (error) {
    configurationError(
      error,
    )
  }
}

export async function submitInquiryForm(
  input:
    InquiryFormInput,
) {
  try {
    requireConsent(
      input.consent,
    )

    const payload:
      InquiryFormPayload =
      {
        formType:
          'inquiry',

        ...submissionMetadata(),

        name:
          input.name,

        email:
          input.email,

        phone:
          input.phone,

        serviceType:
          input.serviceType,

        otherServiceDescription:
          input.otherServiceDescription,

        message:
          input.message,

        website:
          input.website,

        recaptchaToken:
          input.recaptchaToken,
      }

    return await postForm(
      getFormsApiConfig()
        .inquiryUrl,

      payload,
    )
  } catch (error) {
    configurationError(
      error,
    )
  }
}